import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import {
  UserPlus,
  Users,
  Shield,
  ShieldCheck,
  HeadphonesIcon,
  Loader2,
  AlertCircle,
  X,
  Eye,
  EyeOff,
  RefreshCw,
  Lock,
  Unlock,
  UserX,
  UserCheck,
} from 'lucide-react';
import { formatRelativeTime, showToast } from '../utils/helpers';
import * as adminService from '../services/admin';
import {
  AdminUser,
  AdminRole,
  CreateAdminUserDto,
  getCurrentUser,
  isAdmin,
  roleLabels,
  roleColors,
} from '../types/admin';

// ============================================================================
// Role Icon Component
// ============================================================================

const RoleIcon: React.FC<{ role: AdminRole; className?: string }> = ({ role, className = 'h-4 w-4' }) => {
  switch (role) {
    case 'SUPER_ADMIN':
      return <ShieldCheck className={className} />;
    case 'ADMIN':
      return <Shield className={className} />;
    case 'SUPPORT_AGENT':
      return <HeadphonesIcon className={className} />;
  }
};

// ============================================================================
// Create User Modal
// ============================================================================

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  currentUserRole: AdminRole;
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  currentUserRole,
}) => {
  const [formData, setFormData] = useState<CreateAdminUserDto>({
    email: '',
    password: '',
    name: '',
    role: 'SUPPORT_AGENT',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Available roles based on current user's role
  const availableRoles: { value: AdminRole; label: string }[] =
    currentUserRole === 'SUPER_ADMIN'
      ? [
          { value: 'SUPER_ADMIN', label: 'Super Admin' },
          { value: 'ADMIN', label: 'Admin' },
          { value: 'SUPPORT_AGENT', label: 'Support Agent' },
        ]
      : [
          { value: 'ADMIN', label: 'Admin' },
          { value: 'SUPPORT_AGENT', label: 'Support Agent' },
        ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await adminService.createAdminUser(formData);
      showToast('success', `User ${formData.email} created successfully`);
      onSuccess();
      onClose();
      // Reset form
      setFormData({ email: '', password: '', name: '', role: 'SUPPORT_AGENT' });
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to create user';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-card border border-border rounded-xl shadow-2xl w-full max-w-md mx-4 animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
              <UserPlus className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Create Admin User</h2>
              <p className="text-sm text-muted-foreground">Add a new team member</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-primary/10 transition-colors"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Full Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="John Doe"
              className="input"
              required
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="john@xuman.ai"
              className="input"
              required
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Minimum 8 characters"
                className="input pr-10"
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Must be at least 8 characters with uppercase, lowercase, and number
            </p>
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Role
            </label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as AdminRole })}
              className="select"
              required
            >
              {availableRoles.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              {formData.role === 'SUPER_ADMIN' && 'Full access to all features including user management'}
              {formData.role === 'ADMIN' && 'Can manage support tickets and create support agents'}
              {formData.role === 'SUPPORT_AGENT' && 'Can handle support tickets and calls'}
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary flex-1"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary flex-1"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Creating...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Create User
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================================================
// User Row Component
// ============================================================================

interface UserRowProps {
  user: AdminUser;
  currentUser: AdminUser | null;
  onDeactivate: (user: AdminUser) => void;
  onReactivate: (user: AdminUser) => void;
  onUnlock: (user: AdminUser) => void;
}

const UserRow: React.FC<UserRowProps> = ({
  user,
  currentUser,
  onDeactivate,
  onReactivate,
  onUnlock,
}) => {
  const isCurrentUser = currentUser?.id === user.id;
  const isLocked = user.lockedUntil && new Date(user.lockedUntil) > new Date();
  const canManage = currentUser?.role === 'SUPER_ADMIN' || 
    (currentUser?.role === 'ADMIN' && user.role === 'SUPPORT_AGENT');

  return (
    <tr className="border-b border-border hover:bg-primary/5 transition-colors">
      {/* User Info */}
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
            <RoleIcon role={user.role} className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="font-medium text-foreground">
              {user.name}
              {isCurrentUser && (
                <span className="ml-2 text-xs text-muted-foreground">(You)</span>
              )}
            </p>
            <p className="text-sm text-muted-foreground">{user.email}</p>
          </div>
        </div>
      </td>

      {/* Role */}
      <td className="px-6 py-4">
        <span className={`badge border ${roleColors[user.role]}`}>
          <RoleIcon role={user.role} className="h-3 w-3 mr-1" />
          {roleLabels[user.role]}
        </span>
      </td>

      {/* Status */}
      <td className="px-6 py-4">
        <div className="flex flex-col gap-1">
          {user.isActive ? (
            <span className="badge bg-success/20 text-success border border-success/30">
              Active
            </span>
          ) : (
            <span className="badge bg-destructive/20 text-destructive border border-destructive/30">
              Inactive
            </span>
          )}
          {isLocked && (
            <span className="badge bg-warning/20 text-warning border border-warning/30">
              <Lock className="h-3 w-3 mr-1" />
              Locked
            </span>
          )}
        </div>
      </td>

      {/* Last Login */}
      <td className="px-6 py-4 text-sm text-muted-foreground">
        {user.lastLoginAt ? formatRelativeTime(user.lastLoginAt) : 'Never'}
      </td>

      {/* Created */}
      <td className="px-6 py-4 text-sm text-muted-foreground">
        {formatRelativeTime(user.createdAt)}
      </td>

      {/* Actions */}
      <td className="px-6 py-4">
        {canManage && !isCurrentUser && (
          <div className="flex items-center gap-2">
            {isLocked && (
              <button
                onClick={() => onUnlock(user)}
                className="p-2 rounded-lg hover:bg-warning/10 text-warning transition-colors"
                title="Unlock Account"
              >
                <Unlock className="h-4 w-4" />
              </button>
            )}
            {user.isActive ? (
              <button
                onClick={() => onDeactivate(user)}
                className="p-2 rounded-lg hover:bg-destructive/10 text-destructive transition-colors"
                title="Deactivate User"
              >
                <UserX className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={() => onReactivate(user)}
                className="p-2 rounded-lg hover:bg-success/10 text-success transition-colors"
                title="Reactivate User"
              >
                <UserCheck className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
};

// ============================================================================
// Admin Users Page
// ============================================================================

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const currentUser = getCurrentUser();
  const userIsAdmin = isAdmin();

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminService.getAdminUsers();
      setUsers(data);
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to fetch users';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (userIsAdmin) {
      fetchUsers();
    }
  }, [fetchUsers, userIsAdmin]);

  // Check authorization - must be after all hooks
  if (!userIsAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleDeactivate = async (user: AdminUser) => {
    if (!window.confirm(`Are you sure you want to deactivate ${user.name}?`)) return;
    
    try {
      await adminService.deactivateAdminUser(user.id);
      showToast('success', `${user.name} has been deactivated`);
      fetchUsers();
    } catch (err: any) {
      showToast('error', err.response?.data?.message || 'Failed to deactivate user');
    }
  };

  const handleReactivate = async (user: AdminUser) => {
    try {
      await adminService.reactivateAdminUser(user.id);
      showToast('success', `${user.name} has been reactivated`);
      fetchUsers();
    } catch (err: any) {
      showToast('error', err.response?.data?.message || 'Failed to reactivate user');
    }
  };

  const handleUnlock = async (user: AdminUser) => {
    try {
      await adminService.unlockAdminUser(user.id);
      showToast('success', `${user.name}'s account has been unlocked`);
      fetchUsers();
    } catch (err: any) {
      showToast('error', err.response?.data?.message || 'Failed to unlock user');
    }
  };

  // Stats
  const stats = {
    total: users.length,
    active: users.filter((u) => u.isActive).length,
    superAdmins: users.filter((u) => u.role === 'SUPER_ADMIN').length,
    admins: users.filter((u) => u.role === 'ADMIN').length,
    agents: users.filter((u) => u.role === 'SUPPORT_AGENT').length,
  };

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="flex-shrink-0 p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Admin Users</h1>
            <p className="text-muted-foreground mt-1">
              Manage admin accounts and permissions
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchUsers}
              className="btn btn-ghost"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn btn-primary"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Create User
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.total}</p>
                <p className="text-xs text-muted-foreground">Total Users</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/20 flex items-center justify-center">
                <UserCheck className="h-5 w-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.active}</p>
                <p className="text-xs text-muted-foreground">Active</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <ShieldCheck className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.superAdmins}</p>
                <p className="text-xs text-muted-foreground">Super Admins</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Shield className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.admins}</p>
                <p className="text-xs text-muted-foreground">Admins</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                <HeadphonesIcon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.agents}</p>
                <p className="text-xs text-muted-foreground">Support Agents</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {loading && users.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">Failed to load users</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <button onClick={fetchUsers} className="btn btn-primary">
              Try Again
            </button>
          </div>
        ) : (
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead className="bg-card border-b border-border">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">
                    User
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">
                    Role
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">
                    Last Login
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">
                    Created
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    currentUser={currentUser}
                    onDeactivate={handleDeactivate}
                    onReactivate={handleReactivate}
                    onUnlock={handleUnlock}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      <CreateUserModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={fetchUsers}
        currentUserRole={currentUser?.role || 'SUPPORT_AGENT'}
      />
    </div>
  );
};

export default AdminUsers;
